import { useState } from "react"
import { Link } from "react-router-dom"
import { ArrowRight, Check, ChevronDown, ChevronRight } from "lucide-react"
import { useApp } from "../state/AppState"
import { Button, cx } from "../components/ui"
import { Logo } from "../components/brand/Logo"
import { EmailTangle } from "../components/brand/EmailTangle"
import {
	AccessArt,
	HandoverArt,
	LockedArt,
	ProgressArt,
	TrackedList
} from "../components/brand/Illustrations"
import { LaunchPromoBanner, LaunchPromoDialog } from "../components/marketing/LaunchPromo"
import { useInView } from "../lib/useInView"
import { countLabel, fileSize, limitLabel } from "../lib/format"

/**
 * The public page.
 *
 * Benefit first, capability second, picture third - each section opens with
 * what the reader gets, names the feature that delivers it, then draws it.
 *
 * The pictures are illustrations rather than screenshots. A screenshot of a
 * desktop app is four hundred pixels of eight-point text by the time it
 * reaches a phone, and the reader gets nothing from it; a drawing of the same
 * idea at four large elements reads at any width.
 *
 * Everything that moves waits until it is scrolled into view, and holds still
 * for anyone who has asked their system to reduce motion.
 */
export const SUPPORT_EMAIL = "support@getloomina.com"

/**
 * The company that actually takes the payment.
 *
 * It appears on the Stripe checkout page and on the buyer's card statement,
 * so it has to appear here too - somebody who has only ever seen "Loomina"
 * reads an unfamiliar name on their statement as a fraudulent charge. The
 * registered number and office are the trading disclosures a UK company
 * owes on a site it trades under another name from.
 */
export const LEGAL_ENTITY = {
	name: "Enverse Ltd",
	number: "13677271",
	jurisdiction: "England and Wales",
	address: "128 City Road, London EC1V 2NX"
}

export function LandingPage() {
	return (
		<div className="min-h-screen bg-white">
			<SiteNav />
			<main>
				<Hero />
				<BigStatement />
				<Features />
				<Facts />
				<Trust />
				<Pricing />
				<Faq />
				<ClosingCta />
			</main>
			<footer className="border-t border-ink-200 px-5 py-6 sm:px-8">
				<p className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-2xs text-ink-400">
					<span>© {new Date().getFullYear()} Loomina. All rights reserved.</span>
					<span aria-hidden>·</span>
					<a
						href={`mailto:${SUPPORT_EMAIL}`}
						className="font-medium text-ink-500 transition-colors hover:text-brand-700 hover:underline"
					>
						{SUPPORT_EMAIL}
					</a>
				</p>
				<p className="mx-auto mt-2 max-w-2xl text-center text-2xs leading-relaxed text-ink-400">
					Loomina is a product of {LEGAL_ENTITY.name}, registered in{" "}
					{LEGAL_ENTITY.jurisdiction} (company no. {LEGAL_ENTITY.number}).
					<br className="hidden sm:block" /> Registered office:{" "}
					{LEGAL_ENTITY.address}.
				</p>
			</footer>

			<LaunchPromoDialog />
		</div>
	)
}

/* -------------------------------------------------------------------------- */
/* Chrome                                                                      */
/* -------------------------------------------------------------------------- */

const NAV_LINKS = [
	{ href: "#features", label: "Features" },
	{ href: "#pricing", label: "Pricing" },
	{ href: "#faq", label: "FAQ" }
]

function SiteNav() {
	return (
		<header className="sticky top-0 z-40 border-b border-ink-200/70 bg-white/90 backdrop-blur">
			<div className="mx-auto flex h-16 w-full max-w-6xl items-center gap-6 px-5 sm:px-8">
				<Link to="/" aria-label="Loomina" className="flex text-ink-900">
					<Logo size="sm" />
				</Link>

				<nav className="hidden items-center gap-1 md:flex">
					{NAV_LINKS.map((link) => (
						<a
							key={link.href}
							href={link.href}
							className="rounded-lg px-3 py-2 text-[0.8125rem] font-medium text-ink-600 transition-colors hover:bg-ink-50 hover:text-ink-900"
						>
							{link.label}
						</a>
					))}
				</nav>

				<div className="ml-auto flex items-center gap-2">
					<Button as={Link} to="/sign-in" size="sm">
						Sign in
					</Button>
					<Button as={Link} to="/register" variant="primary" size="sm">
						Start free
					</Button>
				</div>
			</div>
		</header>
	)
}

/* -------------------------------------------------------------------------- */
/* Hero                                                                        */
/* -------------------------------------------------------------------------- */

function Hero() {
	return (
		<section className="relative overflow-hidden">
			<div
				className="pointer-events-none absolute inset-x-0 top-0 h-[30rem] bg-gradient-to-b from-brand-50 via-brand-50/50 to-transparent"
				aria-hidden
			/>
			<Blob className="left-[-8rem] top-6 h-80 w-80 bg-brand-200/40" />
			<Blob className="right-[-7rem] top-36 h-72 w-72 bg-state-open/10" />

			<div className="relative mx-auto w-full max-w-6xl px-5 pb-14 pt-14 sm:px-8 sm:pt-20">
				<div className="mx-auto max-w-3xl text-center">
					<h1 className="animate-rise text-[2.5rem] font-semibold leading-[1.05] tracking-tight text-ink-900 sm:text-[4rem]">
						Answers, finally.
					</h1>
					<p
						className="mx-auto mt-6 max-w-2xl animate-rise text-lg leading-relaxed text-ink-600 sm:text-xl"
						style={{ animationDelay: "120ms" }}
					>
						Every question on your project becomes a numbered item with one name
						against it, a status you can read at a glance, and a thread nobody can
						rewrite once it is settled.
					</p>

					<div
						className="mt-8 flex animate-rise flex-wrap items-center justify-center gap-2.5"
						style={{ animationDelay: "220ms" }}
					>
						<Button
							as={Link}
							to="/register"
							variant="primary"
							size="lg"
							iconRight={ArrowRight}
						>
							Start free
						</Button>
						<Button as={Link} to="/sign-in" size="lg">
							Sign in
						</Button>
					</div>
				</div>

				<BeforeAfter />
			</div>
		</section>
	)
}

/** The whole pitch in one row: what you have, and what you get. */
function BeforeAfter() {
	const [ref, inView] = useInView()

	return (
		<div
			ref={ref}
			className="mt-14 grid grid-cols-1 items-center gap-6 lg:grid-cols-[minmax(0,0.8fr)_auto_minmax(0,1.2fr)]"
		>
			<figure className="order-1">
				<EmailTangle className="w-full rounded-2xl shadow-card" />
			</figure>

			<div className="order-2 flex items-center justify-center py-2 lg:py-0">
				<span className="grid h-12 w-12 place-items-center rounded-full bg-brand-700 text-white shadow-lifted">
					<ChevronRight size={22} className="hidden animate-nudge lg:block" aria-hidden />
					<ChevronDown size={22} className="animate-nudge-down lg:hidden" aria-hidden />
				</span>
			</div>

			{/* Held back until the pile-up on the left has played out, so the
			    answer arrives after the problem rather than beside it. */}
			<figure
				className={cx("order-3", inView && "animate-rise")}
				style={{ opacity: inView ? undefined : 0, animationDelay: "1600ms" }}
			>
				<TrackedList className="drop-shadow-sm" />
			</figure>
		</div>
	)
}

function Blob({ className }) {
	return (
		<span
			aria-hidden
			className={cx("pointer-events-none absolute rounded-full blur-3xl", className)}
		/>
	)
}

/** Fades a block up the first time it is scrolled to. */
function Reveal({ delay = 0, className, children }) {
	const [ref, inView] = useInView()
	return (
		<div
			ref={ref}
			className={cx(inView && "animate-rise", className)}
			style={{ opacity: inView ? undefined : 0, animationDelay: `${delay}ms` }}
		>
			{children}
		</div>
	)
}

/* -------------------------------------------------------------------------- */
/* The problem, in large type                                                  */
/* -------------------------------------------------------------------------- */

function BigStatement() {
	return (
		<section className="relative overflow-hidden border-y border-ink-200/70 bg-ink-950">
			<Blob className="left-1/4 top-[-6rem] h-72 w-72 bg-brand-500/25" />
			<Blob className="bottom-[-8rem] right-1/4 h-72 w-72 bg-state-open/20" />
			<div className="relative mx-auto w-full max-w-4xl px-5 py-20 text-center sm:px-8 sm:py-28">
				<Reveal>
					<p className="text-[1.75rem] font-semibold leading-[1.25] tracking-tight text-white sm:text-[2.5rem]">
						A hundred questions across six firms.
						<br className="hidden sm:block" />{" "}
						{/* The chart orange is tuned for a white surface and goes muddy on
						    this one, so the emphasis takes a lighter amber. */}
						<span className="text-amber-400">An inbox cannot track that.</span>
					</p>
					<p className="mx-auto mt-8 max-w-2xl text-lg leading-relaxed text-brand-100 sm:text-xl">
						Give every question its own line, its own owner and its own status. Then
						you can see what is left in three seconds instead of reading a thread.
					</p>
				</Reveal>
			</div>
		</section>
	)
}

/* -------------------------------------------------------------------------- */
/* Features                                                                    */
/* -------------------------------------------------------------------------- */

const FEATURES = [
	{
		eyebrow: "Stop chasing",
		title: "Every question has somebody's name on it",
		body: "Hand a clarification to the person who can settle it and they are emailed the question itself, not a notification about one. The list then says in writing who it is waiting on and since when, so the Monday call stops being a round of “any update on…” and becomes a read of what is left.",
		band: "bg-gradient-to-br from-brand-50 via-white to-state-open-soft/70",
		Art: HandoverArt
	},
	{
		eyebrow: "Settle it once",
		title: "An answer that stays answered",
		body: "Correct your own last reply for as long as you are the last to speak. The moment somebody responds it sets — for you, for them and for the project owner — so six months on the thread still says exactly what was agreed, by whom, and on what date. That is a version you can read out in a meeting, attach to a payment application, or send to a solicitor without checking it first.",
		band: "bg-gradient-to-br from-violet-50 via-white to-brand-50",
		Art: LockedArt,
		reverse: true
	},
	{
		eyebrow: "Share carefully",
		title: "The right people see the right questions",
		body: "Put a team on a project and all of them see all of it, including whatever gets raised next month. Invite a subcontractor to a single item instead and that item is the only thing they will ever see — not the cost conversation two rows above it, and not who else is on the job.",
		band: "bg-gradient-to-br from-state-closed-soft via-white to-brand-50",
		Art: AccessArt
	},
	{
		eyebrow: "Show where you stand",
		title: "Progress you can put on a screen",
		body: "Raised against answered, drawn as the project runs, for one job or all of them at once. Walk into a client review and open a page, instead of spending the morning before it counting rows in a spreadsheet that was already out of date when you started.",
		band: "bg-gradient-to-br from-amber-50 via-white to-state-closed-soft",
		Art: ProgressArt,
		reverse: true
	}
]

function Features() {
	return (
		<section id="features" className="scroll-mt-20">
			{FEATURES.map((feature) => (
				<div key={feature.title} className={cx("border-b border-ink-200/70", feature.band)}>
					<div className="mx-auto w-full max-w-6xl px-5 py-16 sm:px-8 sm:py-20">
						<div
							className={cx(
								"grid grid-cols-1 items-center gap-8 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-14",
								feature.reverse && "lg:[&>*:last-child]:order-first"
							)}
						>
							<Reveal>
								<p className="text-2xs font-semibold uppercase tracking-[0.16em] text-brand-700">
									{feature.eyebrow}
								</p>
								<h3 className="mt-3 text-2xl font-semibold leading-tight tracking-tight text-ink-900 sm:text-[2rem]">
									{feature.title}
								</h3>
								<p className="mt-4 text-base leading-relaxed text-ink-600">
									{feature.body}
								</p>
							</Reveal>
							<Reveal delay={120}>
								<figure className="mx-auto max-w-lg lg:max-w-none">
									<feature.Art className="drop-shadow-sm" />
								</figure>
							</Reveal>
						</div>
					</div>
				</div>
			))}
		</section>
	)
}

/* -------------------------------------------------------------------------- */
/* Facts                                                                       */
/* -------------------------------------------------------------------------- */

const FACTS = [
	{ value: "Free", label: "for everyone you invite" },
	{ value: "One email", label: "to get an answer moving" },
	{ value: "Locked", label: "the second somebody replies" },
	{ value: "Any thread", label: "exports as plain text" }
]

function Facts() {
	return (
		<section className="border-b border-ink-200/70 bg-white">
			<div className="mx-auto w-full max-w-6xl px-5 py-14 sm:px-8">
				<dl className="grid grid-cols-2 gap-8 lg:grid-cols-4">
					{FACTS.map((fact, index) => (
						<Reveal key={fact.value} delay={index * 90}>
							<dt className="text-2xl font-semibold tracking-tight text-brand-800 sm:text-3xl">
								{fact.value}
							</dt>
							<dd className="mt-1 text-[0.875rem] leading-snug text-ink-500">
								{fact.label}
							</dd>
						</Reveal>
					))}
				</dl>
			</div>
		</section>
	)
}

/* -------------------------------------------------------------------------- */
/* Trust                                                                       */
/* -------------------------------------------------------------------------- */

function Trust() {
	return (
		<section className="border-b border-ink-200/70 bg-ink-50">
			<div className="mx-auto w-full max-w-3xl px-5 py-16 text-center sm:px-8 sm:py-20">
				<Reveal>
					<p className="text-2xs font-semibold uppercase tracking-[0.16em] text-brand-700">
						Built to be quoted
					</p>
					<h2 className="mt-3 text-2xl font-semibold tracking-tight text-ink-900 sm:text-3xl">
						A record you can stand behind
					</h2>
					<p className="mt-4 text-base leading-relaxed text-ink-600">
						Comments cannot be deleted and attachments cannot be taken back. Every
						entry carries the name of whoever wrote it and the minute they did, shown
						to each reader in their own timezone. That holds for the project owner as
						much as for anyone they invited.
					</p>
				</Reveal>
			</div>
		</section>
	)
}

/* -------------------------------------------------------------------------- */
/* Pricing                                                                     */
/* -------------------------------------------------------------------------- */

function Pricing() {
	const { config } = useApp()
	const [yearly, setYearly] = useState(false)

	const plans = config?.plans || []
	const symbol = config?.currency_symbol || "$"

	return (
		<section id="pricing" className="scroll-mt-20 border-b border-ink-200/70 bg-white">
			<div className="mx-auto w-full max-w-6xl px-5 py-16 sm:px-8 sm:py-20">
				<Reveal className="text-center">
					<h2 className="text-2xl font-semibold tracking-tight text-ink-900 sm:text-3xl">
						Only the project owner pays
					</h2>
					<p className="mx-auto mt-3 max-w-lg text-base text-ink-600">
						Everyone you invite uses Loomina for free, on every plan.
					</p>

					{plans.length > 0 && <LaunchPromoBanner className="mt-5" />}

					{plans.length > 0 && (
						<div className="mt-7 inline-flex rounded-full border border-ink-200 bg-white p-1">
							<IntervalTab active={!yearly} onClick={() => setYearly(false)}>
								Monthly
							</IntervalTab>
							<IntervalTab active={yearly} onClick={() => setYearly(true)}>
								Yearly
							</IntervalTab>
						</div>
					)}
				</Reveal>

				{plans.length === 0 ? (
					<p className="mt-10 text-center text-[0.8125rem] text-ink-500">
						<Link to="/register" className="font-medium text-brand-700 hover:underline">
							Create an account
						</Link>{" "}
						to see the plans.
					</p>
				) : (
					<div className="mt-10 grid grid-cols-1 items-stretch gap-5 lg:grid-cols-3">
						{plans.map((plan, index) => (
							<Reveal key={plan.key} delay={index * 90} className="h-full">
								<PlanCard
									plan={plan}
									yearly={yearly}
									symbol={symbol}
									featured={plan.key === "plus"}
								/>
							</Reveal>
						))}
					</div>
				)}
			</div>
		</section>
	)
}

function IntervalTab({ active, onClick, children }) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={cx(
				"rounded-full px-4 py-1.5 text-[0.8125rem] font-medium transition-colors",
				active ? "bg-brand-700 text-white" : "text-ink-600 hover:text-ink-900"
			)}
		>
			{children}
		</button>
	)
}

function PlanCard({ plan, yearly, symbol, featured }) {
	const price = yearly ? plan.price_yearly : plan.price_monthly
	const free = !price

	const lines = [
		`${limitLabel(plan.projects)} projects`,
		`${limitLabel(plan.issues_per_project)} questions each`,
		countLabel(plan.teams, "team"),
		`${fileSize(plan.storage_bytes)} of files`,
		plan.digest ? "Daily summary email" : "Email alerts"
	]

	return (
		<div
			className={cx(
				"relative flex h-full flex-col rounded-2xl border bg-white p-6 shadow-card",
				featured ? "border-brand-300 ring-1 ring-brand-300" : "border-ink-200/80"
			)}
		>
			{featured && (
				<span className="absolute -top-2.5 left-6 rounded-full bg-brand-700 px-2.5 py-0.5 text-2xs font-semibold text-white">
					Most chosen
				</span>
			)}

			<h3 className="text-base font-semibold text-ink-900">{plan.name}</h3>
			<p className="mt-4 flex items-baseline gap-1.5">
				<span className="text-4xl font-semibold tracking-tight text-ink-900">
					{free ? "Free" : `${symbol}${formatPrice(price)}`}
				</span>
				{!free && (
					<span className="text-[0.8125rem] text-ink-500">
						/ {yearly ? "year" : "month"}
					</span>
				)}
			</p>

			<Button
				as={Link}
				to="/register"
				variant={featured ? "primary" : "secondary"}
				className="mt-5 w-full"
			>
				{free ? "Start free" : `Choose ${plan.name}`}
			</Button>

			<ul className="mt-6 space-y-2.5 border-t border-ink-100 pt-5">
				{lines.map((line) => (
					<li key={line} className="flex items-start gap-2.5">
						<Check size={14} className="mt-0.5 shrink-0 text-state-closed" aria-hidden />
						<span className="text-[0.8125rem] leading-snug text-ink-700">{line}</span>
					</li>
				))}
			</ul>
		</div>
	)
}

function formatPrice(value) {
	return Number.isInteger(value) ? String(value) : value.toFixed(2)
}

/* -------------------------------------------------------------------------- */
/* FAQ                                                                         */
/* -------------------------------------------------------------------------- */

const QUESTIONS = [
	{
		q: "Does everybody I work with need an account?",
		a: "They need one to take part, and it is free for them — only the person who owns the project is billed. Invite by email and they get a link; if they have no account yet, creating one drops them straight onto the question they were invited to."
	},
	{
		q: "Can somebody delete what they said?",
		a: "No. You can correct your own most recent entry, and only until somebody replies after you. From that point it is fixed for everyone, including the project owner. Comments cannot be deleted and attachments cannot be removed."
	},
	{
		q: "What can an invited person actually see?",
		a: "Exactly the questions they were named on. Not the project around them, not the other questions in it, and not who else is on those. If you need somebody to see a whole project, put them on a team and add that team to the project."
	},
	{
		q: "How is a team different from inviting people one at a time?",
		a: "Inviting adds a person to one clarification. A team is added to the project once, by its owner, and everyone on it sees every question in it — including the ones raised afterwards. It is the right tool for the people actually doing the job."
	},
	{
		q: "Can I show a thread to a client?",
		a: "Yes. Any project or question exports as plain text — the whole thread, attributed and dated, with a link back — so it can be pasted into the email chain it came from, or into a report."
	},
	{
		q: "What happens with people in different countries?",
		a: "Each person sees times in their own timezone, with the zone named, so nobody has to work out what “9am” meant to whoever wrote it. They pick their country at sign-up and can change it later."
	},
	{
		q: "Can I cancel?",
		a: "Any time, from your billing settings. You keep access until the end of the period you have paid for, and your projects stay where they are — read-only beyond what the free plan allows, never deleted."
	}
]

function Faq() {
	return (
		<section id="faq" className="scroll-mt-20 border-b border-ink-200/70 bg-ink-50">
			<div className="mx-auto w-full max-w-3xl px-5 py-16 sm:px-8 sm:py-20">
				<h2 className="text-2xl font-semibold tracking-tight text-ink-900 sm:text-3xl">
					Questions people ask before they start
				</h2>

				<div className="mt-7 divide-y divide-ink-200 border-y border-ink-200">
					{QUESTIONS.map((item) => (
						<details key={item.q} className="group py-4">
							<summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-[0.9375rem] font-medium text-ink-900 marker:hidden">
								{item.q}
								<ChevronDown
									size={17}
									className="mt-0.5 shrink-0 text-ink-400 transition-transform group-open:rotate-180"
									aria-hidden
								/>
							</summary>
							<p className="mt-2.5 pr-8 text-[0.875rem] leading-relaxed text-ink-600">
								{item.a}
							</p>
						</details>
					))}
				</div>

				<p className="mt-6 text-[0.875rem] text-ink-600">
					Still stuck? Email{" "}
					<a
						href={`mailto:${SUPPORT_EMAIL}`}
						className="font-medium text-brand-700 hover:underline"
					>
						{SUPPORT_EMAIL}
					</a>{" "}
					and a person will answer.
				</p>
			</div>
		</section>
	)
}

/* -------------------------------------------------------------------------- */
/* Closing                                                                     */
/* -------------------------------------------------------------------------- */

function ClosingCta() {
	return (
		<section className="bg-gradient-to-br from-brand-950 via-brand-900 to-brand-800">
			<div className="mx-auto w-full max-w-3xl px-5 py-16 text-center sm:px-8 sm:py-20">
				<Reveal>
					<h2 className="text-[1.75rem] font-semibold leading-tight tracking-tight text-white sm:text-4xl">
						Start with the chain you are stuck in
					</h2>
					<p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-brand-100">
						Pull out the questions still outstanding in it, invite the people who owe
						you an answer, and watch the list get shorter.
					</p>
					<div className="mt-8 flex flex-wrap items-center justify-center gap-2.5">
						<Button
							as={Link}
							to="/register"
							variant="onDarkPrimary"
							size="lg"
							iconRight={ArrowRight}
						>
							Start free
						</Button>
						<Button as={Link} to="/sign-in" variant="onDark" size="lg">
							Sign in
						</Button>
					</div>
				</Reveal>
			</div>
		</section>
	)
}
